import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { FacilityRule } from "@src/appV2/Facilities/types";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  BottomSheetIllustrationContent,
  List,
  ListItem,
  mergeSxProps,
  Text,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import pluralize from "pluralize";
import { useEffect, useRef, useState } from "react";

interface WorkplaceRulesReminderContentProps {
  workplaceRules: FacilityRule[];
  onContinue: () => void;
  shiftId: string;
  workplaceId: string;
}

const numbersToWords: Record<number, string> = {
  1: "One",
  2: "Two",
  3: "Three",
  4: "Four",
  5: "Five",
  6: "Six",
  7: "Seven",
  8: "Eight",
  9: "Nine",
};

function getTitle(rulesCount: number): string {
  const baseTitle = `Important ${pluralize("Reminder", rulesCount)} from Your Workplace`;

  const numberWord = numbersToWords[rulesCount];
  if (isDefined(numberWord)) {
    return `${numberWord} ${baseTitle}`;
  }

  return baseTitle;
}

export function WorkplaceRulesReminderContent(props: WorkplaceRulesReminderContentProps) {
  const { workplaceRules, onContinue, shiftId, workplaceId } = props;

  return (
    <Stack
      sx={(theme) => ({
        ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
        minHeight: 0,
      })}
    >
      <BottomSheetIllustrationContent
        illustrationType="info"
        title={
          <Text variant="h3" textAlign="center">
            {getTitle(workplaceRules.length)}
          </Text>
        }
      />

      <ScrollArea sx={{ minHeight: 0 }}>
        <List dense sx={(theme) => ({ paddingX: theme.spacing(6) })}>
          {workplaceRules.map((rule) => (
            <ListItem key={rule._id} sx={{ justifyContent: "center" }}>
              <Text variant="body1" textAlign="center">
                {rule.text}
              </Text>
            </ListItem>
          ))}
        </List>
      </ScrollArea>

      <DialogFooter orientation="vertical">
        <Text variant="body1" color="error" maxWidth={240} textAlign="center" mb={2}>
          Repeat failure to follow facility rules may result in DNRs
        </Text>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.CLOCK_IN_WORKPLACE_RULES_ACKNOWLEDGED, {
              shiftId,
              workplaceId,
              rulesCount: workplaceRules.length,
            });

            onContinue();
          }}
        >
          Continue
        </Button>
      </DialogFooter>
    </Stack>
  );
}

interface ScrollAreaProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

function ScrollArea({ children, sx }: ScrollAreaProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(true);

  const handleScroll = (): void => {
    if (!scrollRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const scrollGap = 10;

    setShowTopGradient(scrollTop > scrollGap);
    setShowBottomGradient(scrollTop < scrollHeight - clientHeight - scrollGap);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    handleScroll();
    scrollElement.addEventListener("scroll", handleScroll);

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const commonGradientProps: SxProps = {
    position: "absolute",
    left: 0,
    right: 0,
    height: 48,
    zIndex: 10,
    pointerEvents: "none",
    transition: "opacity 100ms ease",
  };

  return (
    <Box
      sx={mergeSxProps(
        {
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
        sx
      )}
    >
      <Box
        sx={mergeSxProps(commonGradientProps, (theme) => ({
          top: 0,
          // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
          background: `linear-gradient(to bottom, ${
            theme.palette.background.tertiary ?? "white"
          } 5%, transparent)`,
          opacity: showTopGradient ? 1 : 0,
        }))}
      />
      <Box
        sx={mergeSxProps(commonGradientProps, (theme) => ({
          bottom: 0,
          // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
          background: `linear-gradient(to top, ${
            theme.palette.background.tertiary ?? "white"
          } 5%, transparent)`,
          opacity: showBottomGradient ? 1 : 0,
        }))}
      />
      <Box
        ref={scrollRef}
        sx={{
          overflowY: "auto",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
